import { getApi } from 'common/api-common'
import { blobbyStrings } from './strings'
import { planPrice } from 'common/utils/planPrice'
import { PlanSlug } from 'common/planSlugs'
import { Format } from 'common/utils'
import { Constants } from 'common/utils/constants'

const defaultStrings = {
  averageSetupTime: 'Average set up time of ten minutes with no setup fees',
  averageSetupTimeDescription: `<p><h4 class="fs-fluid-16-18 fw-bold">
How long does setting up an account take?
</h4></p><p>
Account creation couldn't be easier. Simply register your account with a valid payment method and log in to our customer portal to complete verification. Using advanced AML and KYC technology, we automatically conduct the necessary checks to verify your account. Most accounts are verified instantly but in some cases, if enhanced due diligence is required, verification may take slightly longer and will be reviewed by our trained AML staff.
</p><p><h4 class="fs-fluid-16-18 fw-bold">
How much does it cost to set up an account?
</h4></p><p>
Setting up an account is free. There are no setup fees when joining the Hoxton Mix Virtual Office address.
</p>`,
  asicRegisteredAddressAu:
    'ASIC official company registered address included for free',
  asicRegisteredAddressAuDescription: `<p>
You can use the Hoxton Mix Virtual Office address as the legal registered address for your business with ASIC. This is fully included in the price of your plan.
</p>`,
  companiesHouseRegisteredAddress:
    'Companies House official company registered address included for free',
  companiesHouseRegisteredAddressDescription: `<p>
You can use the Hoxton Mix Virtual Office address as the legal registered address for your business with Companies House. This is fully included in the prices of all our plans. You do not need to pay anything extra.
</p>`,
  emailAlerts: 'Instant alerts for every piece of post',
  emailAlertsDescription: `<p>
  Every time you receive a piece of mail, we automatically send you an email alert.  Make sure you download the Hoxton Mix mobile app for instant mail notifications and put your business in the palm of your hand.
</p>`,
  howVirtualOfficePackagesWorkContent: `<div class="text-semibold text-body">Sign up with The Hoxton Mix for your virtual office address in London, and use it for all of your formal communications. You take our London business address and apply it to your business, both for your customers and all HMRC correspondence. Register it with Companies House, and we will become your registered business address in London.</div>
<br/><div>Above and beyond the usual virtual office address, we also provide an AI-powered mail management system called HaiRo™.</div>
<br/>
<div>This is a unique service that only we provide, and it’s included in the same price plan. Your mail gets sent to the virtual office address, and then HaiRo™ processes all of your physical mail, scans it, summarises it and then sends it to you digitally. Just download the app, or review it in the customer portal - you will never lose an item again, searching your post becomes super simple and important messages never go amiss.</div>
<br/>
<ul class="gap-8 d-flex flex-column">
<li>Your mail is processed and sent to you in a digital format within 1 working day. Our processing facility is fully secure, and our proprietary AI mail processing system reduces processing errors and ensures you post stays private.
</li>
<li>
With a Hoxton Mix Virtual Office, you can run your business from anywhere in the world and still have a prime London company address to use on your website, marketing materials, and more.
</li>
<li>
Affordable and simple pricing: The Hoxton Mix provides the perfect simple-to-understand solution for companies looking for a cheap virtual office in London.
</li>
<li>
Your physical mail is securely stored for 30 days before it is securely and professionally destroyed; you can come and collect your mail or have it forwarded to you within that time frame. Forwarding comes with an additional fee and is available for valid addresses anywhere in the UK and the rest of the world.
</li>
</ul>`,
  howVirtualOfficePackagesWorkDescription:
    'All the benefits of a London business address without hassle',
  howVirtualOfficePackagesWorkTitle: `How our <span class='text-gradient-purple'>virtual office</span><br/>services work`,
  legalAddressForHMRC:
    'Legal address for HMRC correspondence included for free',
  legalAddressForHMRCDescription: `<p>
You can use the Hoxton Mix Virtual Office address as the legal registered address for your business for all your HMRC correspondents. This is fully included in the prices of all our plans. You do not need to pay anything extra.
</p>`,

  receiveYourBusinessLetters: 'Receive all your business letters',
  receiveYourBusinessLettersDescription: `<p>You can use the Hoxton Mix Virtual Office to receive all your business’ post. This is fully included in the prices of all our plans. You do not need to pay anything extra.
</p><p>
We cannot accept parcels on behalf of your business.
</p>`,
  safeCancelling: 'Safe cancellation — cancel any time',
  safeCancellingDescription: `<p>This means you don’t have to worry about nasty contract renewal surprises.
</p><p>
<h4 class="fs-fluid-16-18 fw-bold">When can I cancel a monthly plan?</h4>
</p><p>
The monthly renewal subscription fees will continue to be billed to the Payment Method you provided, automatically until cancelled. You must cancel your subscription before it renews each month to avoid billing of the next month's subscription fee to the Payment Method you provided.
</p><p>
Refunds cannot be claimed for any partial-month subscription period.
</p><p><h4 class="fs-fluid-16-18 fw-bold">
When can I cancel an annual plan?
</h4></p><p>
You can cancel at any point and you will not be charged for any subsequent years. We are not able to offer pro-rata refunds for the annual plan. This allows us to offer two months free when signing up to the annual plan. If you cancel within seven days of your most recent invoice we will refund you for that invoice should you wish to terminate your service immediately.
</p><p><h4 class="fs-fluid-16-18 fw-bold">
How easy is it to cancel?
</h4></p><p>
Very easy. Just send an email to our support team (<a href="mailto:help@hoxtonmix.com">help@hoxtonmix.com</a>) from your registered email address asking for a cancellation and we will cancel your account.
</p>`,
  topQualityPostCode:
    'Prestigious business address and postcode with excellent office building appearance',
  topQualityPostCodeDescription: `<p><h4 class="fs-fluid-16-18 fw-bold">Where is the Hoxton Mix Virtual Office address located?
</h4></p><p>
The Hoxton Mix Virtual Office is a prime address in central London. It is close to Old Street, Moorgate and Liverpool Street tube stations.
</p><p><h4 class="fs-fluid-16-18 fw-bold">
What does the office building look like?
</h4></p><p>
  <img loading="lazy" class="d-block" src="/images/office.png"/>
  <small>The Hoxton Mix building is the white and grey one. Actual Google Street View image.</small>
</p><p>
This is a photo of the exterior of our building. This image is visible on Google Street view when people look up your address.
</p><p><h4 class="fs-fluid-16-18 fw-bold">
What will my new address be?
</h4></p><p>
Your Company Name / Your Name,<br/>
${Constants.companyInformation.address2},<br/>
${Constants.companyInformation.city},<br/>
${Constants.companyInformation.postal_code}<br/>
</p>`,
  topQualityPostCodeAuDescription: `<p><h4 class="fs-fluid-16-18 fw-bold">Where is the Hoxton Mix Virtual Office address located?
</h4></p><p>
${Constants.companyInformation.address2Au} ${Constants.companyInformation.cityAu}, ${Constants.companyInformation.postal_codeAu}
</p><p><h4 class="fs-fluid-16-18 fw-bold">
What will my new address be?
</h4></p><p>
${Constants.companyInformation.address2Au} ${Constants.companyInformation.cityAu} ${Constants.companyInformation.postal_codeAu}
</p>`,
  whyChooseUs:
    '<span class="d-xl-block">Why choose us for a</span> <span class="text-gradient-purple">London Virtual Office</span>?',
  whyChooseUsForward:
    'Why choose the</span> <br className="d-none d-lg-block" /> <span class="text-gradient-purple">Mail forwarding add-on?',
}
export const virtualOfficeStrings = getApi().makeStrings({
  en: defaultStrings,
  es: blobbyStrings(defaultStrings),
})
